"use client";

import { SCHEDULE_MONTHS } from "@/data/calculator-data";
import { formatPrice, useCurrency } from "@/lib/currency";
import type { CalcResult, Locale } from "@/types/calculator";
import { Download } from "lucide-react";
import { useLocale } from "next-intl";

/** Estimated schedule table (first 6 months). */
export default function CalcSchedule({ schedule }: { schedule: CalcResult["schedule"] }) {
  const locale = useLocale() as Locale;
  const ar = locale === "ar";
  const { code } = useCurrency();
  const nf = new Intl.NumberFormat(ar ? "ar-EG" : "en-US");

  return (
    <div className="mt-10 border border-black/10 dark:border-white/10">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-black/10 p-5 dark:border-white/10">
        <h3 className="text-sm font-semibold text-ash dark:text-white">
          {ar ? "جدول السداد التقديري (أول ٦ أشهر)" : "Estimated schedule (first 6 months)"}
        </h3>
        <button className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-accent">
          <Download className="h-4 w-4" />
          {ar ? "حمّل PDF" : "Download PDF"}
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] text-sm">
          <thead>
            <tr className="border-b border-black/10 text-start text-[11px] uppercase tracking-widest text-ash/50 dark:border-white/10 dark:text-white/50">
              <th className="p-4 text-start">{ar ? "الشهر" : "Month"}</th>
              <th className="p-4 text-start">{ar ? "التاريخ المتوقع" : "Expected date"}</th>
              <th className="p-4 text-start">{ar ? "قيمة القسط" : "Amount"}</th>
              <th className="p-4 text-start">{ar ? "الرصيد المتبقي" : "Balance"}</th>
            </tr>
          </thead>
          <tbody>
            {schedule.map((r) => (
              <tr key={r.index} className="border-b border-black/5 last:border-0 dark:border-white/5">
                <td className="p-4 text-ash dark:text-white">{r.isDown ? (ar ? "٠ (المقدم)" : "0 (Down)") : nf.format(r.index)}</td>
                <td className="p-4 text-ash/60 dark:text-white/60">{SCHEDULE_MONTHS[r.monthOffset]?.[locale]}</td>
                <td className="p-4 font-semibold text-ash dark:text-white">{formatPrice(r.amount, code, locale)}</td>
                <td className="p-4 text-ash/60 dark:text-white/60">{formatPrice(r.balance, code, locale)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
